<template>
  <div>
    <SectionSeparatorWrapper
      :headerText="referenceData.additionalInformationPartnerHeading.informationText"
      class="field"
      v-if="oldCompositionHavePartner"
      :separator="false"
      headerRequiredFieldsText="indicates required field"
    >
      <h3 class="h6 sub-heading-underlined">Partner detail</h3>
      <FormField :helperText="referenceData.additionalInformationUpdateDetails.informationText" :noLabelOverride="true">
        <MemberTag :firstName="partner.firstName" :lastName="partner.lastName" :showLastName="true" relationship="Partner" />
      </FormField>
      <FormField :labelText="referenceData.additionalInformationKeepPartner.label" :helperText="referenceData.additionalInformationKeepPartner.helpText">
        <ul class="radio-group large">
          <li v-for="option in displayRadioOptions" :key="`veteran-card-${option.display}`">
            <input
              type="radio"
              :id="`keep-adult-${option.display}`"
              v-model.lazy="partner.keepAdult"
              :name="`veteran-card-${option.display}`"
              :value="option.returnValue"
              @change="resetCardDetails() && !partner.keepAdult"
              :disabled="true"
            />
            <label :for="`keep-adult-${option.display}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
      <!-- Partner is an un-insured adult message -->
      <FormField v-if="isPartnerNotAnInsuredAdult">
        <Alert alertType="info" class="inline-message" message="If you need to terminate an uninsured partner, please contact Defence Health." />
      </FormField>
      <!-- Partner will no longer be covered & holds eligibility messages and acknowledgement checkbox -->
      <FormField v-if="!partner.keepAdult">
        <!-- This adult will no longer be covered on this policy message -->
        <Alert alertType="info" class="inline-message" message="This adult will no longer be covered on this policy." />
        <!-- If partner holds policy eligibility and is being removed from the policy -->
        <template v-if="showEligibilityAlert()">
          <Alert alertType="info" class="inline-message" :message="'Please note, the partner you are attempting to terminate holds the eligibility for this policy.'" />
          <input
            type="checkbox"
            id="acknowledgedEligibilityChange"
            name="acknowledgedEligibilityChange"
            v-model="acknowledgedEligibilityChange"
            @change="v$.acknowledgedEligibilityChange.$touch()"
            @keyup.enter.space="acknowledgedEligibilityChange = !acknowledgedEligibilityChange"
          />
          <label for="acknowledgedEligibilityChange">I understand that removing the adult changes the eligibility of the remaining policy members.</label>
        </template>

        <!-- Partner is rebate applicant and is being removed from the policy -->
        <template v-if="isRebateApplicantBeingTerminated">
          <Alert
            alertType="info"
            class="inline-message"
            :message="`You can’t remove the partner on this policy as they currently hold the rebate on this policy. Please contact Defence Health to either switch the rebate applicant, withdraw the rebate or terminate the partner on the policy.`"
          />
        </template>
      </FormField>

      <template v-if="!isPartnerRebateApplicant">
        <!-- Reason for termination -->
        <FormField v-if="!partner.keepAdult" class="form-section" labelText="Reason for termination" :required="!partner.keepAdult">
          <DropdownSelect
            :items="terminationReasonsMemberList"
            v-model="partner.terminationReason"
            name="partner-termination-reason"
            :id="`partner-termination-reason-${partner.terminationReason}`"
            :initialSelect="getDropdownOption(terminationReasonsMemberList, partner.terminationReason, 'Please select a reason')"
          />
        </FormField>
        <!-- New fund name -->
        <FormField
          v-if="!partner.keepAdult && transferToFundValues.includes(partner.terminationReason)"
          class="form-section"
          labelText="New fund name"
          :required="!partner.keepAdult && transferToFundValues.includes(partner.terminationReason)"
        >
          <DropdownSelect
            :items="fundNamesTerminated"
            v-model="partner.fundName"
            name="partner-fund-name"
            :id="`partner-fund-name-${partner.fundName}`"
            :initialSelect="getDropdownOption(fundNamesTerminated, partner.fundName, 'Please select a health fund')"
          />
        </FormField>
      </template>
    </SectionSeparatorWrapper>

    <!-- Transferring hospital from another fund -->
    <SectionSeparatorWrapper v-if="showTransferOtherFundQuestion && partner.keepAdult && !showNewPartnerSection" :separator="false" class="field">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Transferring from another fund</h3>
          <FormField labelText="Are you transferring from another fund?" :helperText="referenceData.additionalInformationTransferringFundHistory.informationText">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`from-other-funds-existing-partner-${option.display}`">
                <input
                  type="radio"
                  :disabled="showTransferOtherFundQuestion"
                  :id="`from-other-funds-existing-partner-${option.display}`"
                  v-model.lazy="partner.transferFromOthers"
                  :name="`from-other-funds-existing-partner-${option.display}`"
                  :value="option.returnValue"
                />
                <label :for="`from-other-funds-partner-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
        <template v-if="partner.transferFromOthers">
          <div class="form-section" v-for="(fund, index) in partner.selectedInsuranceFund" :key="index">
            <FormField labelText="Which fund are you transferring from?" required class="form-section">
              <DropdownSelect
                :items="fundNamesNewPerson"
                v-model="fund.name"
                name="existing-partner-fund-name"
                :id="`existing-partner-new-fundName-${index}`"
                :initialSelect="getDropdownOption(fundNamesNewPerson, fund.name, 'Please select a health fund')"
              />
            </FormField>
            <FormField
              labelText="What was your member number at your previous health fund?"
              placeholder="XXXXXXXXX"
              type="text"
              class="form-section"
              :id="`existing-partner-fund-number-${index}`"
              v-model.lazy="fund.number"
              required
              :error="v$.partner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number?.length > 0"
              :errorMessage="getFundErrorMessage(v$.partner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number || [])"
            />
            <FormField>
              <p v-html="informationReferenceData.insuranceFundInformationText.informationText" />
            </FormField>

            <div v-if="partner.selectedInsuranceFund.length > 1" class="form-section">
              <a @click.prevent="removeSelectField(partner.selectedInsuranceFund, index)" class="always-fresh" href="#" role="button">Remove fund</a>
            </div>
          </div>

          <div class="form-section">
            <a
              v-if="partner.selectedInsuranceFund.length < 2"
              @click.prevent="addAnotherSelectField(partner.selectedInsuranceFund)"
              role="button"
              class="always-fresh"
              href="#"
            >
              Add another fund to switch from
            </a>
          </div>
        </template>
      </SectionSeparatorWrapper>

    <!-- Existing partner has keep private on informative message -->
    <template v-if="doesPartnerHaveKeepPrivateOn">
      <FormField>
        <Alert
          alertType="info"
          class="inline-message"
          message="The partner on this policy has the ‘keep private’ flag enabled, you are not able to view or update their additional information such as their member details, concession card or veteran card information. Your partner will be required to contact Defence Health to update their member details."
        />
      </FormField>
    </template>

    <!-- New partner details -->
    <SectionSeparatorWrapper
      v-if="(!partner.keepAdult || showNewPartnerSection) && !isRebateApplicantBeingTerminated"
      :headerText="!oldCompositionHavePartner ? referenceData.additionalInformationPartnerHeading.informationText : null"
      class="field"
      :separator="false"
      :headerRequiredFieldsText="!oldCompositionHavePartner ? 'indicates required field' : null"
    >
      <div v-if="apiStatus.duplicatePersonCheck.loading" class="loading-screen">
        <div class="form-grid">
          <Spinner></Spinner>
        </div>
      </div>

      <h3 class="h6 sub-heading-underlined">New partner details</h3>
      <div class="form-section">
        <FormField>
          <p>The member told us to change who is covered on the membership.</p>
          <p>Capture the details for the adult being added to the membership.</p>
        </FormField>
      </div>

      <div class="form-section">
        <FormField
          labelText="First name"
          placeholder="e.g. John"
          type="text"
          key="newPartnerFirstName"
          id="new-partner-first-name"
          v-model.lazy="v$.partner.newPartner.firstName.$model"
          :error="v$.partner.newPartner.firstName.$error"
          :errorMessage="getNameErrorMessage('firstName')"
          :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
          @input="searchDebounce(runDuplicatePersonSearch)"
          @blur="runDuplicatePersonSearch()"
          required
        />
      </div>
      <div class="form-section">
        <FormField
          labelText="Middle name"
          placeholder="e.g. Fred"
          type="text"
          id="new-partner-middle-name"
          v-model.lazy="v$.partner.newPartner.middleName.$model"
          :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
          :error="v$.partner.newPartner.$error"
          :errorMessage="getNameErrorMessage('middleName')"
          :required="false"
        />
      </div>
      <div class="form-section">
        <FormField
          id="new-partner-last-name"
          labelText="Last name"
          placeholder="e.g. Citizen"
          type="text"
          required
          v-model.lazy="v$.partner.newPartner.lastName.$model"
          :error="v$.partner.newPartner.lastName.$error"
          :errorMessage="getNameErrorMessage('lastName')"
          :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
          @input="searchDebounce(runDuplicatePersonSearch)"
          @blur="runDuplicatePersonSearch()"
        />
      </div>
      <FormField labelText="Sex" :helperText="informationReferenceData.sex.helpText" class="form-section">
        <ul class="radio-group large">
          <li v-for="sex in informationReferenceData.sex.options" :key="sex.returnValue">
            <input
              :id="sex.returnValue"
              :value="sex.returnValue"
              :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
              type="radio"
              v-model="v$.partner.newPartner.gender.$model"
              @change="runDuplicatePersonSearch()"
            />
            <label :for="sex.returnValue">{{ sex.display }}</label>
          </li>
        </ul>
      </FormField>
      <FormField
        id="new-partner-dob"
        labelText="Date of birth"
        class="form-section half-width"
        placeholder="DD / MM / YYYY"
        v-maska="'## / ## / ####'"
        type="text"
        v-model.lazy.trim="v$.partner.newPartner.DOB.$model"
        key="dateOfBirth"
        maxlength="14"
        :isDateInput="true"
        :error="v$.partner.newPartner.DOB.$error"
        :errorMessage="getDOBErrorMessage(v$.partner.newPartner.DOB)"
        :disabled="true"
        @input="searchDebounce(runDuplicatePersonSearch)"
        @blur="runDuplicatePersonSearch()"
        required
      />
      <div class="form-grid">
        <ApiLoading v-if="partner.newPartner.duplicateCheckFailed" :apiError="partner.newPartner.duplicateCheckFailed">
          <p>
            Sorry, we couldn’t load the information.
            <a @click="runDuplicatePersonSearch()" class="always-fresh">Reload</a>
            the result to continue
          </p>
        </ApiLoading>
      </div>
      <!-- Duplicate alert message-->
      <FormField v-if="partner.newPartner.isDuplicate">
        <Alert alertType="error" :message="DUPLICATE_PERSON_ERROR_MESSAGE" />
      </FormField>
      <!-- Preferred phone number -->
      <div v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed" class="form-section">
        <ContactInfo
          :key="partner.newPartner.preferredContactMethodsValid"
          :initialState="partner.newPartner.preferredContactMethods"
          :getRequired="true"
          :phoneNumberTypeList="informationReferenceData.phoneTypeOptions"
          :labelText="'Preferred phone number'"
          :id="'partner'"
          @updatePreferredContactMethods="updatePreferredContactMethods"
        />
      </div>

      <!-- email -->
      <FormField
        v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed"
        labelText="Email address"
        key="newPartnerEmail"
        type="text"
        class="form-section"
        placeholder="info@defencehealth.com.au"
        required
        v-model.lazy="v$.partner.newPartner.email.$model"
        :error="v$.partner.newPartner.email.$error"
        :errorMessage="getEmailErrorMessage()"
      />
      <FormField v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed" class="form-section">
        <FormLabel labelText="Confirm email address" required forId="confirm-email" />
        <FormInput
          type="text"
          placeholder="info@defencehealth.com.au"
          required
          id="confirm-email"
          v-model.lazy="v$.partner.newPartner.confirmEmail.$model"
          :overrideClass="v$.partner.newPartner.confirmEmail.$error ? 'error' : null"
        />
        <label
          for="confirm-email"
          class="error"
          v-if="v$.partner.newPartner.confirmEmail.$error & v$.partner.newPartner.confirmEmail.IS_INVALID.$invalid && v$.partner.newPartner.confirmEmail.required"
        >
          {{ EmailErrorMessage.NOT_MATCH }}
        </label>
        <label for="confirm-email" class="error" v-if="v$.partner.newPartner.confirmEmail.$error & v$.partner.newPartner.confirmEmail.required.$invalid">
          {{ EmailErrorMessage.IS_EMPTY }} Confirm email address
        </label>
      </FormField>
    </SectionSeparatorWrapper>
    <!-- Address -->
    <SectionSeparatorWrapper
      v-if="
        ((hasAddressStateChanged && !doesPartnerHaveKeepPrivateOn) || (showNewPartnerSection && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed)) &&
        !isRebateApplicantBeingTerminated
      "
      class="field"
      :separator="false"
    >
      <div class="form-section">
        <h3 class="h6 sub-heading-underlined">Address</h3>
        <AddressValidator
          @updateAddressDetails="updateAddressDetail"
          @updateAddressValidation="updateAddressValidation"
          :preFilledResidentialAddress="showNewPartnerSection ? null : partner.residentialAddress"
          :preFilledResidentialState="showNewPartnerSection ? null : changeCoverData.step1.state"
          :preFilledPostalAddress="showNewPartnerSection ? null : partner.postalAddress"
          :preFilledPOBox="showNewPartnerSection ? null : partner.POBox"
          :preFillledDifferentPostalAddress="showNewPartnerSection ? false : partner.differentPostalAddress"
          :isPartner="true"
          :stateLocked="true"
          :isChangeCover="true"
          :isExistingMember="!showNewPartnerSection"
          :showPostalAlert="postalAddressUpdated && (multiplePolicyHolderMemberships || multipleMembershipsAndAtLeastOnePolicyHolder)"
          :postalAddressHelpText="referenceData.postalAddressHelpText.informationText"
          showPostalAddressHelperText
        ></AddressValidator>
      </div>
    </SectionSeparatorWrapper>

    <!-- Transferring from another fund -->
    <SectionSeparatorWrapper
      v-if="showNewPartnerSection && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed && !isRebateApplicantBeingTerminated"
      :separator="false"
      class="field"
    >
      <div class="form-section">
        <h3 class="h6 sub-heading-underlined">Transferring from another fund</h3>
        <FormField
          labelText="Are you transferring from another fund?"
          :helperText="showTransferOtherFundQuestion ? referenceData.additionalInformationTransferringFundHistory.informationText : null"
        >
          <ul class="radio-group large">
            <li v-for="option in displayRadioOptions" :key="`from-other-funds-partner-${option.display}`">
              <input
                type="radio"
                :disabled="showTransferOtherFundQuestion"
                :id="`from-other-funds-partner-${option.display}`"
                v-model.lazy="partner.newPartner.transferFromOthers"
                :name="`from-other-funds-partner-${option.display}`"
                :value="option.returnValue"
                @change="!partner.newPartner.transferFromOthers && resetTransferFundDetails()"
              />
              <label :for="`from-other-funds-partner-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
        </FormField>
      </div>
      <template v-if="partner.newPartner.transferFromOthers">
        <div class="form-section" v-for="(fund, index) in partner.newPartner.selectedInsuranceFund" :key="index">
          <FormField labelText="Which fund are you transferring from?" required class="form-section">
            <DropdownSelect
              :items="fundNamesNewPerson"
              v-model="fund.name"
              name="partner-fund-name"
              :id="`partner-new-fundName-${index}`"
              :initialSelect="getDropdownOption(fundNamesNewPerson, fund.name, 'Please select a health fund')"
            />
          </FormField>
          <FormField
            labelText="What was your member number at your previous health fund?"
            placeholder="XXXXXXXXX"
            type="text"
            class="form-section"
            :id="`new-partner-fund-number-${index}`"
            v-model.lazy="fund.number"
            required
            :error="v$.partner.newPartner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number?.length > 0"
            :errorMessage="getFundErrorMessage(v$.partner.newPartner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number || [])"
          />
          <FormField>
            <p v-html="informationReferenceData.insuranceFundInformationText.informationText" />
          </FormField>

          <div v-if="partner.newPartner.selectedInsuranceFund.length > 1" class="form-section">
            <a @click.prevent="removeSelectField(partner.newPartner.selectedInsuranceFund, index)" class="always-fresh" href="#" role="button">Remove fund</a>
          </div>
        </div>

        <div class="form-section">
          <a
            v-if="partner.newPartner.selectedInsuranceFund.length < 2"
            @click.prevent="addAnotherSelectField(partner.newPartner.selectedInsuranceFund)"
            role="button"
            class="always-fresh"
            href="#"
          >
            Add another fund to switch from
          </a>
        </div>
      </template>
    </SectionSeparatorWrapper>
    <!-- Veteran card -->
    <SectionSeparatorWrapper v-if="showVeteranCardSection && !isRebateApplicantBeingTerminated" :separator="false">
      <div class="form-section">
        <h3 class="h6 sub-heading-underlined">Veteran card</h3>
        <FormField labelText="Do you have a Veteran card?" :helperText="referenceData.additionalInformationUpdateVeteranDetails.informationText">
          <ul class="radio-group large">
            <li v-for="option in displayRadioOptions" :key="`partner-veteran-card-${option.display}`">
              <input
                type="radio"
                disabled
                :id="`partner-veteran-card-${option.display}`"
                v-model.lazy="partner.veteranCard.haveVeteranCard"
                :name="`partner-veteran-card-${option.display}`"
                :value="option.returnValue"
                @change="haveVeteranCardUpdated"
              />
              <label :for="`partner-veteran-card-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
        </FormField>
      </div>
      <FormField v-if="partner.veteranCard.haveVeteranCard" :required="partner.veteranCard.haveVeteranCard" labelText="Which Veteran card do you hold?" class="form-section">
        <ul class="radio-group large form-section veteran-card-type">
          <li v-for="option in informationReferenceData.veteranCardTypeOptions" :key="option.returnValue">
            <input
              type="radio"
              disabled
              :id="option.returnValue"
              :value="option.returnValue"
              name="veteran-card-partner"
              v-model.lazy="v$.partner.veteranCard.veteranCardType.$model"
              @change="veteranCardTypeUpdated"
            />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
        <p v-if="v$.partner.veteranCard.veteranCardType.$model === 'Veteran Gold Card'" v-html="informationReferenceData.veteranCardGoldTypeInformationText.informationText" />
        <template v-if="v$.partner.veteranCard.veteranCardType.$model === 'Veteran White Card'">
          <p class="form-section" v-html="informationReferenceData.veteranCardConditionInformationText.informationText" />
          <MultiSelect
            name="white card options"
            id="veteran-card-condition-options-partner"
            :items="informationReferenceData.veteranCardConditionOptions"
            @updateData="updateVeteranCardOptions"
            :selectedItems="partner.veteranCard.veteranCardConditionOptions"
            placeholderText="Search for a listed accepted condition"
            ref="partner.veteranCardConditionOptions"
            :maximum="informationReferenceData.veteranCardConditionOptions.length"
          ></MultiSelect>
        </template>
      </FormField>
    </SectionSeparatorWrapper>

    <!-- Concession card -->
    <SectionSeparatorWrapper v-if="showConcessionCardSectionForPartner" :separator="false">
      <div class="form-section">
        <h3 class="h6 sub-heading-underlined">Concession card</h3>
        <FormField labelText="Do you have a concession card?">
          <ul class="radio-group large">
            <li v-for="option in displayRadioOptions" :key="`partner-concession-card-${option.display}`">
              <input
                type="radio"
                :id="`partner-concession-card-${option.display}`"
                v-model="partner.haveConcessionCard"
                :name="`partner-concession-card-${option.display}`"
                :value="option.returnValue"
              />
              <label :for="`partner-concession-card-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
        </FormField>
      </div>
      <template v-if="showConcessionCardDetailsForm">
        <div class="form-section" v-for="(concession, index) in partner.selectedConcessionCardOptions" :key="index">
          <FormField>
            <p>
              Advising us whether you hold a concession card ensures we are not paying for services that you are already entitled to, which helps us keep premiums more affordable.
            </p>
          </FormField>
          <FormField class="form-section" labelText="Which concession card do you hold?">
            <DropdownSelect
              initialSelect="Please select a concession card"
              :items="getSelectOptionsLeft(index, partner.selectedConcessionCardOptions, informationReferenceData.concessionCardOptions)"
              v-model="concession.name"
              @input="cardDetailUpdated(index, cardDetailUpdatedType.TYPE)"
            />
          </FormField>
          <FormField
            labelText="Card number"
            placeholder="XXXXXXXXX"
            type="text"
            class="form-section"
            v-model.lazy="concession.number"
            :error="v$.partner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number?.length > 0"
            :errorMessage="getConcessionNumberErrorMessage(v$.partner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number || [])"
            @input="cardDetailUpdated(index, cardDetailUpdatedType.NUMBER)"
          />
          <FormField
            labelText="Expiry date"
            type="text"
            class="form-section half-width"
            v-model.lazy="concession.expire"
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            :required="false"
            :error="v$.partner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire?.length > 0"
            :errorMessage="getConcessionExpiryErrorMessage(v$.partner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire || [])"
            @input="cardDetailUpdated(index, cardDetailUpdatedType.EXPIRY_DATE)"
          />
          <div v-if="partner.selectedConcessionCardOptions.length > 1" class="form-section">
            <a @click.prevent="removeConcessionSelectField(partner.selectedConcessionCardOptions, index)" class="always-fresh" role="button" href="#">Remove card</a>
          </div>
          <div class="form-section">
            <a
              v-if="partner.selectedConcessionCardOptions.length < informationReferenceData.concessionCardOptions.length"
              @click.prevent="addAnotherSelectField(partner.selectedConcessionCardOptions, true)"
              role="button"
              class="always-fresh"
              href="#"
            >
              Add another concession card
            </a>
          </div>
        </div>
      </template>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { email, helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DATE_VALIDATOR, JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { Alert, ApiLoading, FormField, FormInput, FormLabel, MemberTag, SectionSeparatorWrapper } from '@/components';
import { AddressValidator, ContactInfo, DropdownSelect, MultiSelect, Spinner } from '@general/js/components';
import { CoverTypeValue, NeedsAnalysisEligibility, EmailErrorMessage, NameErrorMessage, LHCOverruleReasons } from '@general/js/enums';
import {
  formatFormDate,
  getCurrentDate,
  invalidConcessionCardNumber,
  isPastDate,
  isPastDateIncludingToday,
  overAge,
  stringWithInvalidSpace,
  stringWithSymbols,
  underAge,
} from '@general/js/utils';
import {
  showConcessionCardSection as showConcessionCard,
  showConcessionCardDetailsForm as showConcessionCardDetails,
  sortByObjectProperty,
  formatPhoneNumber,
  getProductName,
} from '@/utils';
import { ConcessionCardTypes, EligibilityTypes, OperationType, VeteranCardTypeOptions } from '@/enums';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';

let triggerDuplicateSearch;
const apiTriggerTime = 1000;

export default {
  name: 'Partner',

  setup() { return { v$: useVuelidate() }; },

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    AddressValidator,
    Alert,
    ApiLoading,
    ContactInfo,
    DropdownSelect,
    FormField,
    FormInput,
    FormLabel,
    MemberTag,
    MultiSelect,
    SectionSeparatorWrapper,
    Spinner,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
    terminationReasons: {
      type: Object,
      required: true,
    },
    // Fund names using `dhl_hiccode` when adding a new partner
    fundNamesNewPerson: {
      type: Array,
      required: true,
    },
    // Fund names using `dhl_code` when terminating a partner
    fundNamesTerminated: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      acknowledgedEligibilityChange: null,
      partner: {
        keepAdult: true,
        firstName: '',
        middleName: '',
        lastName: '',
        terminationReason: null,
        fundName: null,
        abd: null,
        lhc: null,
        transferFromOthers: null,
        selectedInsuranceFund: [{ name: '', number: '' }],
        newPartner: {
          firstName: '',
          middleName: '',
          lastName: '',
          gender: '',
          DOB: '',
          preferredContactMethods: [],
          preferredContactMethodsValid: false,
          email: '',
          confirmEmail: '',
          transferFromOthers: null,
          selectedInsuranceFund: [{ name: '', number: '' }],
          // duplicate check variables
          isDuplicate: false,
          duplicateCheckFailed: false,
          differentPostalAddress: false,
          residentialAddress: {},
          postalAddress: {},
          POBox: null,
          addressesInvalid: false,
        },
        differentPostalAddress: false,
        residentialAddress: {},
        postalAddress: {},
        preferredContactMethods: [],
        POBox: null,
        addressesInvalid: false,
        veteranCard: {
          haveVeteranCard: null,
          veteranCardType: '',
          veteranCardConditionOptions: [],
        },
        haveConcessionCard: null,
        selectedConcessionCardOptions: [{ name: '', number: '', expire: '' }],
      },
      eligibleForLHC: false, // default
      parentClassName: 'partner',
      abdLhcResponseValid: false, // default
      displayRadioOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      cardDetailUpdatedType: {
        TYPE: 'cardType',
        NUMBER: 'cardNumber',
        EXPIRY_DATE: 'expiryDate',
        VETERAN_TYPE: 'veteranType',
        ACCEPTED_CONDITION: 'acceptedCondition',
      },
      isActive: false,
      EmailErrorMessage,
    };
  },

  beforeMount() {
    this.partner = { ...this.partner, ...this.partnerStep };

    // only prefill partner dob from 'my details' if old composition did not have partner and current partner dob is empty string
    if (!this.oldCompositionHavePartner && this.newCompositionHavePartner && !this.partner.newPartner.DOB) {
      this.partner.newPartner.DOB = this.partnerDOB;
    }

    // Elected to remove Partner in "My Cover" and added DOB for new Partner
    if (!this.changeCoverEnteredData.step2.partner.keepAdult && this.changeCoverEnteredData.step2.partner.newPartner.DOB) {
      this.partner.newPartner.DOB = this.changeCoverEnteredData.step2.partner.newPartner.DOB;
    }

    if (this.changeCoverEnteredData.step2.partner.keepAdult) {
      this.partner.transferFromOthers = this.changeCoverEnteredData.step2.partner.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
    } else {
      this.partner.newPartner.transferFromOthers = this.changeCoverEnteredData.step2.partner.newPartner.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
    }

    if (this.changeCoverData.step2.partner?.veteranCard?.haveVeteranCard !== null || this.changeCoverData.step2.partner?.newPartner?.veteranCard?.haveVeteranCard !== null) {
      const veteranDetails = this.changeCoverData.step2.partner.keepAdult
        ? this.changeCoverData.step2.partner.veteranCard
        : this.changeCoverData.step2.partner.newPartner.veteranCard;

      this.partner.veteranCard = { ...veteranDetails };

      if (this.partner.veteranCard?.veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER) {
        // Map the accepted conditions pre-filled in My Cover from ref data for multi select
        this.partner.veteranCard.veteranCardConditionOptions =
          veteranDetails?.veteranCardConditionOptions?.map((condition) => {
            return {
              ...condition,
              display: this.getVeteranCardDisplayValue(this.captureInformationReferenceData.veteranCardConditionOptions, condition.returnValue),
            };
          }) ?? [];
      } else {
        this.partner.veteranCard.veteranCardConditionOptions = [];
      }
      this.veteranCardTypeUpdated();
    }
  },

  activated() {
    this.isActive = true;

    // ensure that validation is triggered (because there could be prefilled information)
    this.v$.partner.veteranCard.veteranCardType.$touch();
    if (this.partnerStep.haveConcessionCard) {
      this.v$.partner.selectedConcessionCardOptions.$touch();
    }
    this.partner.residentialAddress = { ...this.policyHolderStep.residentialAddress }; // ensures that partner is also updated when policy holder address changes
    if (this.showNewPartnerSection) {
      this.partner.newPartner.residentialAddress = { ...this.policyHolderStep.residentialAddress };
    }
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: this.partner,
              isInvalid:
                newData.$invalid ||
                newData.$error ||
                (!this.partner.keepAdult && (this.partner.newPartner.isDuplicate || this.partner.newPartner.duplicateCheckFailed)) ||
                (!this.isPolicyHolderEligible && !this.partner.keepAdult && !this.acknowledgedEligibilityChange),
            });
          });
        }
      },
      deep: true,
    },
    'apiStatus.duplicatePersonCheck.loading': {
      handler() {
        this.$emit('apiLoading', { apiLoading: this.apiStatus.duplicatePersonCheck.loading });
      },
    },
    'partner.keepAdult': {
      handler(newVal) {
        if (newVal && this.currentEligibility === this.changeCoverData.step1.eligibility) {
          // Keep the current partner, reset eligibility to initial
          this.updateEligibilityStep(this.getEligibilityInitialData);
        }
      },
    },
    'partner.haveConcessionCard': function () {
      if (!this.partner.keepAdult || this.showNewPartnerSection) {
        if (!this.showConcessionCardDetailsForm) {
          this.partner.selectedConcessionCardOptions = [];
        } else {
          this.partner.selectedConcessionCardOptions = [{ name: '', number: '', expire: '' }];
        }
      } else {
        // If there are initial cards for an existing partner then prefill fields
        if (this.initialData.partner.selectedConcessionCardOptions.length > 0) {
          this.partner.selectedConcessionCardOptions = this.initialConcessionCards;
        }

        if (this.showConcessionCardSectionForPartner && !this.showConcessionCardDetailsForm) {
          this.partner.selectedConcessionCardOptions = [];
        }
      }
      // revalidate the form
      this.v$.partner.selectedConcessionCardOptions.$touch();
    },
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    terminationReasonsMemberList() {
      const memberTerminationReasonOptions = [...this.terminationReasons.memberReasons, ...this.terminationReasons.bothReasons];
      return sortByObjectProperty(memberTerminationReasonOptions, 'description').reverse();
    },

    validationObj() {
      const partnerObj = {
        acknowledgedEligibilityChange: {
          required: requiredIf(function () {
            return !this.isPolicyHolderEligible && !this.partner.keepAdult;
          }),
        },
        partner: {
          addressesInvalid: {
            isValid(bool) {
              if (!this.hasAddressStateChanged && (this.partner.keepAdult || this.showNewPartnerSection)) {
                return true;
              }
              return bool === false;
            },
          },
          terminationReason: {
            required: requiredIf(function () {
              return !this.partner.keepAdult;
            }),
          },
          fundName: {
            required: requiredIf(function () {
              if (!this.partner.keepAdult) {
                if (this.transferToFundValues.includes(this.partner.terminationReason) && this.partner.fundName === null) {
                  return true;
                }
              }
              return false;
            }),
          },
          veteranCard: {
            veteranCardType: {
              required: requiredIf(function () {
                if (this.showVeteranCardSection) {
                  // only required if I/partner has/is serving
                  if (this.partner.veteranCard.haveVeteranCard === null) {
                    return true;
                  }
                  return this.partner.veteranCard.haveVeteranCard;
                }
                return false;
              }),
            },
            veteranCardConditionOptions: {
              areConditionsSelected(selectedOptions) {
                // For white veteran card, at least 1 accepted condition should be selected
                if (
                  this.partner.veteranCard.haveVeteranCard === true &&
                  this.partner.veteranCard.veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER &&
                  this.showVeteranCardSection
                ) {
                  return selectedOptions.length > 0;
                }
                return true;
              },
            },
          },
          selectedInsuranceFund: {
            $each: helpers.forEach({
              name: {
                required: requiredIf(function () {
                  return this.partner.keepAdult && this.showTransferOtherFundQuestion && !this.showNewPartnerSection;
                }),
              },
              number: {
                required: requiredIf(function () {
                  return this.partner.keepAdult && this.showTransferOtherFundQuestion && !this.showNewPartnerSection;
                }),
                stringWithSymbols,
                maxLength: maxLength(19),
              },
            }),
          },

          selectedConcessionCardOptions: {
            $each: helpers.forEach({
              name: {
                required: requiredIf(function () {
                  if (this.showNewPartnerSection && this.showConcessionCardSection) {
                    if (this.partner.haveConcessionCard === null) {
                      return true;
                    }
                  } else if (!this.showConcessionCardSectionForPartner) {
                    // For existing partners where concession card section is not showing
                    return false;
                  }
                  return this.showConcessionCardDetailsForm;
                }),
              },
              number: {
                required: requiredIf(function () {
                  if (this.showNewPartnerSection && this.showConcessionCardSection) {
                    if (this.partner.haveConcessionCard === null) {
                      return true;
                    }
                  } else if (!this.showConcessionCardSectionForPartner) {
                    return false;
                  }
                  return this.showConcessionCardDetailsForm;
                }),
                invalidConcessionCardNumber: (value, option) => {
                  if (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC) {
                    // TODO: Add these checks to ConcessionUtils file
                    return invalidConcessionCardNumber(value);
                  }
                  return true;
                },
              },
              expire: {
                isPastDate: (value, option) => {
                  if (
                    value !== '' &&
                    (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC) &&
                    this.showConcessionCardSectionForPartner &&
                    this.showConcessionCardDetailsForm
                  ) {
                    // TODO: Add these checks to concessionUtils file
                    return !isPastDateIncludingToday(value);
                  }
                  return true;
                },
                IS_INVALID: (value) => {
                  return DATE_VALIDATOR(value);
                },
              },
            }),
          },
        },
      };

      if (!this.partner.keepAdult || this.showNewPartnerSection) {
        partnerObj.partner.newPartner = {
          firstName: {
            required,
            JOIN_NOW_NAME_VALIDATOR,
            stringWithInvalidSpace,
            maxLength: maxLength(160),
          },
          middleName: {
            JOIN_NOW_NAME_VALIDATOR,
            stringWithInvalidSpace,
            maxLength: maxLength(160),
          },
          lastName: {
            required,
            JOIN_NOW_NAME_VALIDATOR,
            stringWithInvalidSpace,
            maxLength: maxLength(160),
          },
          gender: {
            required,
          },
          DOB: {
            required,
            DATE_VALIDATOR,
            overAge,
            underAge,
            isPastDate,
          },
          email: {
            email,
            required,
          },
          confirmEmail: {
            required,
            IS_INVALID: (value, vm) => {
              return value === vm.email;
            },
          },
          preferredContactMethodsValid: {
            isValidated(value) {
              return value === true;
            },
          },
          selectedInsuranceFund: {
            $each: helpers.forEach({
              name: {
                required: requiredIf(function () {
                  if (this.partner.newPartner.transferFromOthers === null) {
                    return true;
                  }
                  return this.partner.newPartner.transferFromOthers;
                }),
              },
              number: {
                required: requiredIf(function () {
                  if (this.partner.newPartner.transferFromOthers === null) {
                    return true;
                  }
                  return this.partner.newPartner.transferFromOthers;
                }),
                stringWithSymbols,
                maxLength: maxLength(19),
              },
            }),
          },
          isDuplicate: () => {
            if (this.partner.newPartner.isDuplicate) {
              // fail validation by returning false if member is duplicate so that the member cannot proceed with the Change Cover flow
              return false;
            }
            return true;
          },
        };
      }
      return partnerObj;
    },

    eligibleForABD() {
      const products = this.selectedProducts.products.map((obj) => {
        return obj.eligibleForABD;
      });

      return products.some(Boolean) || false;
    },

    isExServingOrServing() {
      return this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.SERVING || this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.EX_SERVING;
    },

    showVeteranCardSection() {
      return ((this.hasEligibilityChanged && !this.doesPartnerHaveKeepPrivateOn) || this.showNewPartnerSection) && this.isExServingOrServing;
    },

    isRebateApplicantBeingTerminated() {
      return this.isPartnerRebateApplicant && this.partner.keepAdult === false;
    },

    showConcessionCardSection() {
      const addressState = this.changeCoverData.step1.state;
      const coverType = this.changeCoverData.step2.coverType;
      const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
      return showConcessionCard(addressState, this.partnerDOB, extrasOnly);
    },

    showConcessionCardDetailsForm() {
      const addressState = this.changeCoverData.step1.state;
      const coverType = this.changeCoverData.step2.coverType;
      const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
      return showConcessionCardDetails(addressState, this.partnerDOB, extrasOnly, this.partner.haveConcessionCard);
    },

    showTransferOtherFundQuestion() {
      if (this.partner.keepAdult && !this.showNewPartnerSection) {
        return this.changeCoverEnteredData.step2.partner.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
      }
      return this.changeCoverEnteredData.step2.partner.newPartner.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
    },

    initialConcessionCards() {
      const validConcessionCardTypes = [];

      this.initialData.partner.selectedConcessionCardOptions.forEach((concessionCard) => {
        if (concessionCard.name in ConcessionCardTypes) {
          validConcessionCardTypes.push(concessionCard);
        } else {
          validConcessionCardTypes.push({ name: '', number: '', expire: '' });
        }
      });

      return validConcessionCardTypes;
    },

    showConcessionCardSectionForPartner() {
      const showConcessionCardSection = this.showConcessionCardSection && !this.isRebateApplicantBeingTerminated;

      if (this.showNewPartnerSection) {
        return this.showNewPartnerSection && !this.partner.newPartner.isDuplicate && !this.partner.newPartner.duplicateCheckFailed && showConcessionCardSection;
      }
      return !this.doesPartnerHaveKeepPrivateOn && (this.hasAddressStateChanged || this.hasEligibilityChanged) && showConcessionCardSection;
    },

    postalAddressUpdated() {
      if (!this.showNewPartnerSection) {
        const initialPostalAddress = this.initialData.partner.postalAddress ?? {};
        // Check if any field in the postal address has changed
        return !isEqual(initialPostalAddress, this.partner.postalAddress);
      }
      return false;
    },
  },

  methods: {
    searchDebounce(fn) {
      clearTimeout(triggerDuplicateSearch);
      triggerDuplicateSearch = setTimeout(async () => {
        await fn();
      }, apiTriggerTime);
    },

    async runDuplicatePersonSearch() {
      // only run if not already loading
      if (!this.apiStatus.duplicatePersonCheck.loading) {
        const firstNameInvalid = this.v$.partner.newPartner.firstName.$invalid;
        const lastNameInvalid = this.v$.partner.newPartner.lastName.$invalid;
        const genderInvalid = this.v$.partner.newPartner.gender.$invalid;
        const dobInvalid = this.v$.partner.newPartner.DOB.$invalid;
        // only run if all four fields are valid
        if (!firstNameInvalid && !lastNameInvalid && !genderInvalid && !dobInvalid) {
          const currentFirstName = this.partner.newPartner.firstName;
          const currentLastName = this.partner.newPartner.lastName;
          const currentGender = this.partner.newPartner.gender;
          const currentDOB = formatFormDate(this.partner.newPartner.DOB);

          let isPersonDuplicate = false;
          try {
            this.partner.newPartner.duplicateCheckFailed = false;
            isPersonDuplicate = await this.duplicatePersonSearch(currentFirstName, currentLastName, currentGender, formatFormDate(currentDOB));
            this.partner.newPartner.isDuplicate = isPersonDuplicate;
          } catch (error) {
            logger.log(error);
            this.partner.newPartner.duplicateCheckFailed = true;
            this.partner.newPartner.isDuplicate = false;
          }

          // If duplicate, need to raise a duplicate person work item in CRM
          if (isPersonDuplicate) {
            const payload = {
              firstName: currentFirstName,
              lastName: currentLastName,
              dob: currentDOB,
              sex: currentGender,
              needsAnalysisInfo: { ...this.getNeedAnalysisRecommendationsPayload },
            };
            const request = {
              workItemCategory: 'Duplicate Person Check Member Portal',
              title: 'Duplicate Contact in Change Cover Request',
              payload: { payload },
            };
            // make api call (will need to await if error action is required)
            this.raiseWorkItem(request);
          }
        }
      }
    },

    updatePreferredContactMethods(data) {
      this.partner.newPartner.preferredContactMethods = data.phoneNumbersArr;
      this.partner.newPartner.preferredContactMethodsValid = !data.isInvalid;
    },

    updateAddressValidation(data) {
      this.partner.POBox = data.isPOBox;
      this.partner.differentPostalAddress = data.differentPostalAddress;
    },

    updateAddressDetail(addressDetail, type, isInvalid) {
      if (!this.showNewPartnerSection) {
        this.partner.addressesInvalid = isInvalid;
        this.partner.differentPostalAddress = addressDetail.differentPostalAddress;
        if (type === 'residential') {
          this.partner.residentialAddress = { ...addressDetail.address };
          if (!addressDetail.differentPostalAddress) {
            this.partner.postalAddress = { ...addressDetail.address };
          }
        } else if (type === 'postal') {
          if (addressDetail.differentPostalAddress) {
            this.partner.postalAddress = { ...addressDetail.address };
          } else {
            this.partner.postalAddress = { ...this.partner.residentialAddress };
          }
        }
      } else {
        this.partner.newPartner.addressesInvalid = isInvalid;
        this.partner.newPartner.differentPostalAddress = addressDetail.differentPostalAddress;
        if (type === 'residential') {
          this.partner.newPartner.residentialAddress = { ...addressDetail.address };
          if (!addressDetail.differentPostalAddress) {
            this.partner.newPartner.postalAddress = { ...addressDetail.address };
          }
        } else if (type === 'postal') {
          if (addressDetail.differentPostalAddress) {
            this.partner.newPartner.postalAddress = { ...addressDetail.address };
          }
        }
      }
    },

    resetTransferFundDetails() {
      if (this.partner.newPartner.selectedInsuranceFund.length > 0) {
        this.partner.newPartner.selectedInsuranceFund = [{ name: '', number: '' }];
      }
    },

    haveVeteranCardUpdated() {
      const initialVeteranCard = this.initialData.partner.veteranCard;
      const newVeteranCard = this.partner.veteranCard;

      if (initialVeteranCard.haveVeteranCard === true) {
        if (newVeteranCard.haveVeteranCard === false) {
          initialVeteranCard.op = OperationType.REMOVE;
        } else {
          initialVeteranCard.op = OperationType.NONE;
        }
      }
    },

    updateVeteranCardOptions(data) {
      const existingAcceptedConditions = cloneDeep(this.initialData.partner.veteranCard.veteranCardConditionOptions);
      const acceptedConditions = [];

      // If there are no current accepted conditions selected
      if (data.length === 0 && existingAcceptedConditions) {
        this.initialData.partner.veteranCard.veteranCardConditionOptions.forEach((condition) => {
          condition.op = OperationType.REMOVE;
        });
      }
      // if there are accepted conditions selected
      else {
        if (existingAcceptedConditions) {
          this.initialData.partner.veteranCard.veteranCardConditionOptions.forEach((initialCondition) => {
            data.forEach((condition) => {
              if (initialCondition.returnValue === condition.returnValue) {
                initialCondition.op = OperationType.NONE;
                condition.op = OperationType.NONE;
              }
            });
          });
        }

        // Check if one or more initial accepted conditions have been de-selected.
        const removedExistingConditions = existingAcceptedConditions.filter((initialCondition) => {
          return !data.some((condition) => {
            return initialCondition.returnValue === condition.returnValue;
          });
        });

        if (removedExistingConditions.length > 0) {
          removedExistingConditions.forEach((removedCondition) => {
            this.initialData.partner.veteranCard.veteranCardConditionOptions.forEach((initialCondition) => {
              if (initialCondition.returnValue === removedCondition.returnValue) {
                initialCondition.op = OperationType.REMOVE;
              }
            });
          });
        }

        data.forEach((condition) => {
          let indexOfExistingCondition = -1; // Default

          if (existingAcceptedConditions) {
            // Check if the current condition is actually an existing condition.
            indexOfExistingCondition = existingAcceptedConditions.findIndex((existingCondition) => {
              return existingCondition.returnValue === condition.returnValue;
            });

            // If it is an existing condition, re-add it to the list of latest accepted conditions using the initial details.
            if (indexOfExistingCondition > -1) {
              acceptedConditions.push({
                id: existingAcceptedConditions[indexOfExistingCondition].id,
                returnValue: existingAcceptedConditions[indexOfExistingCondition].returnValue,
                display: this.getDisplayValue(existingAcceptedConditions[indexOfExistingCondition].returnValue, this.informationReferenceData.veteranCardConditionOptions),
                startDate: existingAcceptedConditions[indexOfExistingCondition].startDate,
                endDate: existingAcceptedConditions[indexOfExistingCondition].originalEndDate,
                originalEndDate: existingAcceptedConditions[indexOfExistingCondition].originalEndDate,
                op: OperationType.NONE,
              });
              this.initialData.partner.veteranCard.veteranCardConditionOptions[indexOfExistingCondition].endDate =
                existingAcceptedConditions[indexOfExistingCondition].originalEndDate;
            }

            // Else, treat this as an entirely new accepted condition.
            if (indexOfExistingCondition === -1) {
              acceptedConditions.push({
                id: null, // New accepted conditions that have not yet been submitted to OHI have no ID.
                returnValue: condition.returnValue,
                display: this.getDisplayValue(condition.returnValue, this.informationReferenceData.veteranCardConditionOptions),
                startDate: getCurrentDate(), // Always set to today's date.
                endDate: null,
                originalEndDate: null,
                op: OperationType.ADD,
              });
            }
          }
        });
      }
      this.partner.veteranCard = { ...this.partner.veteranCard, veteranCardConditionOptions: acceptedConditions };
    },

    resetVeteranCardDetails() {
      this.partner.veteranCard.haveVeteranCard = null;
      this.partner.veteranCard.veteranCardType = null;
      this.partner.veteranCard.veteranCardConditionOptions = [];
    },

    resetConcessionCardDetails() {
      this.partner.haveConcessionCard = null;
      if (this.partner.selectedConcessionCardOptions.length > 0) {
        this.partner.selectedConcessionCardOptions = [{ name: '', number: '', expire: '' }];
      }
    },

    resetCardDetails() {
      if (!this.partner.keepAdult) {
        this.resetVeteranCardDetails();
        this.resetConcessionCardDetails();
      } else {
        this.partner.veteranCard = cloneDeep(this.initialData.partner.veteranCard);
        this.partner.haveConcessionCard = this.initialData.partner.haveConcessionCard;
        this.partner.selectedConcessionCardOptions = this.initialData.partner.selectedConcessionCardOptions;
      }
    },

    // get the actual display value from the relative api data list
    getDisplayValue(modelValue, listName) {
      let displayValue = '';
      listName.forEach((el) => {
        if (el.returnValue === modelValue) {
          displayValue = el.display;
        }
      });

      return displayValue;
    },

    // To add another object for selected contact, concession card, or insurance fund
    addAnotherSelectField(selectedOptions, isConcession) {
      const firstSelection = JSON.parse(JSON.stringify(selectedOptions[0]));
      Object.keys(firstSelection).forEach((key) => {
        firstSelection[key] = '';
      });

      if (isConcession) {
        firstSelection.op = OperationType.ADD;
        firstSelection.startDate = getCurrentDate();
        firstSelection.id = null;
      }
      selectedOptions.push(firstSelection);
    },

    // To remove an object from selected contact, or insurance fund
    removeSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }
    },

    removeConcessionSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }

      // set to replace in initial data
      this.initialData.partner.selectedConcessionCardOptions.forEach((card) => {
        if (selectedData[0].name === card.name) {
          card.op = OperationType.REMOVE;
        }
      });
    },

    veteranCardTypeUpdated() {
      const initialCardType = this.initialData.partner.veteranCard.veteranCardType;
      const targetCardType = this.partner.veteranCard.veteranCardType;

      if (this.initialData.partner.veteranCard.haveVeteranCard) {
        if (initialCardType !== targetCardType) {
          this.initialData.partner.veteranCard.op = OperationType.REMOVE;
          this.partner.veteranCard.op = OperationType.ADD;
          this.partner.veteranCard.startDate = getCurrentDate();
        } else {
          const isWhiteVeteranCard = initialCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER && targetCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER;
          const hasChangedConditions = this.partner.veteranCard.veteranCardConditionOptions.some((condition) => {
            return condition.op !== OperationType.NONE;
          });

          if (isWhiteVeteranCard && hasChangedConditions) {
            this.partner.veteranCard.op = OperationType.REPLACE;
          } else {
            this.initialData.partner.veteranCard.op = OperationType.NONE;
            this.partner.veteranCard.op = OperationType.NONE;
            this.partner.veteranCard.startDate = this.initialData.partner.veteranCard.startDate;
          }
        }
      } else {
        this.partner.veteranCard.op = OperationType.ADD;
        this.partner.veteranCard.startDate = getCurrentDate();
      }
    },

    cardDetailUpdated(cardIndex, updateType) {
      const targetCardObj = this.partner.selectedConcessionCardOptions[cardIndex];
      const initialCardList = this.initialData.partner.selectedConcessionCardOptions;

      let cardIndexInitialList;
      initialCardList.forEach((card, index) => {
        const sameIds = card.id === targetCardObj.id;
        const sameTypeAndNumber = card.name === targetCardObj.name && card.number === targetCardObj.number;
        if (sameIds || sameTypeAndNumber) {
          cardIndexInitialList = index;
        }
      });

      if (cardIndexInitialList !== undefined) {
        const initialCardObj = initialCardList[cardIndexInitialList];

        switch (updateType) {
          case this.cardDetailUpdatedType.TYPE:
            if (!isEqual(targetCardObj.name, initialCardObj.name)) {
              initialCardObj.op = OperationType.REMOVE;
              targetCardObj.op = OperationType.ADD;
            } else {
              initialCardObj.op = OperationType.NONE;
              targetCardObj.op = OperationType.NONE;

              if (!isEqual(targetCardObj.expire, initialCardObj.expire)) {
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.REPLACE;
              }

              if (!isEqual(targetCardObj.number, initialCardObj.number)) {
                initialCardObj.op = OperationType.REMOVE;
                targetCardObj.op = OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.NUMBER:
            // Only excute following logic when card number input is valid
            if (this.v$.partner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number?.length > 0) {
              if (!isEqual(targetCardObj.expire, initialCardObj.expire) && isEqual(targetCardObj.number, initialCardObj.number)) {
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.REPLACE;
              } else if (
                ['date', 'number', 'name'].every((key) => {
                  return targetCardObj[key] === initialCardObj[key];
                })
              ) {
                // if newly input card number is same as initial card number and both expiry date and card type are same
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.NONE;
              } else {
                initialCardObj.op = OperationType.REMOVE;
                targetCardObj.op = OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.EXPIRY_DATE:
            // only update `op` to replace when it is not newly added
            if (targetCardObj.op !== OperationType.ADD && !isEqual(targetCardObj.expire, initialCardObj.expire)) {
              targetCardObj.op = OperationType.REPLACE;
            }
            break;
          default:
            break;
        }

        switch (targetCardObj.op) {
          case OperationType.ADD:
            targetCardObj.startDate = getCurrentDate();
            break;

          default:
            targetCardObj.startDate = initialCardList[cardIndex].startDate;
            break;
        }

        const targetCardToCompare = cloneDeep(targetCardObj);
        const targetCardInitialToCompare = cloneDeep(initialCardObj);

        delete targetCardToCompare.op;
        delete targetCardInitialToCompare.op;

        if (isEqual(targetCardToCompare, targetCardInitialToCompare)) {
          initialCardObj.op = OperationType.NONE;
          targetCardObj.op = OperationType.NONE;
        }

        if (targetCardObj.op === OperationType.ADD) {
          targetCardObj.id = null;
        } else {
          targetCardObj.id = initialCardObj.id;
        }
      }
    },

    getNameErrorMessage(keyName) {
      if (keyName !== 'middleName') {
        if (this.v$.partner.newPartner[keyName].$error && this.v$.partner.newPartner[keyName].maxLength.$invalid) {
          return NameErrorMessage.IS_OVER_LENGTH;
        }
        if (
          this.v$.partner.newPartner[keyName].$error &&
          (this.v$.partner.newPartner[keyName].JOIN_NOW_NAME_VALIDATOR.$invalid || !stringWithInvalidSpace(this.v$.partner.newPartner[keyName].$model))
        ) {
          return NameErrorMessage.IS_INVALID;
        }
        if (this.v$.partner.newPartner[keyName].$error && this.v$.partner.newPartner[keyName].required.$invalid) {
          if (keyName === 'firstName') return `${NameErrorMessage.IS_EMPTY} First name.`;
          return `${NameErrorMessage.IS_EMPTY} Last name.`;
        }

        return '';
      }
      if (this.v$.partner.newPartner.middleName.$error && this.v$.partner.newPartner.middleName.maxLength.$invalid) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (this.v$.partner.newPartner.middleName.$error && this.v$.partner.newPartner.middleName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return NameErrorMessage.IS_INVALID;
      }
      return '';
    },

    getEmailErrorMessage() {
      if (this.v$.partner.newPartner.email.$error && this.v$.partner.newPartner.email.required.$invalid) {
        return `${EmailErrorMessage.IS_EMPTY} Email address`;
      }
      return EmailErrorMessage.IS_INVALID;
    },
    getFundErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please include a response for Member number.';
      if (errors.some(e => e.$validator === 'maxLength' || e.$validator === 'stringWithSymbols')) return 'Please enter a combination of up to 19 letters or numbers.';
      return '';
    },

    getConcessionNumberErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please provide a response for Card number.';
      if (errors.some(e => e.$validator === 'invalidConcessionCardNumber')) return 'Please enter 9 numbers and a letter.';
      return '';
    },

    getConcessionExpiryErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'IS_INVALID')) return 'Please enter the expiry date using the format DD/MM/YYYY.';
      if (errors.some(e => e.$validator === 'isPastDate')) return 'Please try again, expiry dates must be a future date.';
      return '';
    },

    showEligibilityAlert() {
      const hasServed = [...EligibilityTypes.EX_SERVING, ...EligibilityTypes.SERVING].includes(this.currentEligibility);
      return !this.partner.keepAdult && !this.isPolicyHolderEligible && hasServed;
    },

    formatPhoneNumber,
    getProductName,
  },
};
</script>

<style lang="scss" scoped>
:deep(section.focusable) {
  & > .separator-wrapper {
    row-gap: 5px;
  }

  .separator-content {
    .form-section:not(:last-child) {
      margin-bottom: rem(25);
    }
  }
}

.box-alert {
  margin-bottom: 0;
}

.bottom-margin {
  margin-bottom: 10px;
}

:deep(.box) {
  &.alert:last-child {
    margin-bottom: 0;
  }
}

:deep(.dropdown-select-wrapper) {
  overflow: visible;
}

.loading-screen {
  position: absolute;
  background: $WHITE;
  backdrop-filter: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  opacity: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: center;
  top: 50%;
  @include desktop {
    column-gap: $desktop_spacing;
  }

  @include tablet {
    column-gap: $tablet_spacing;
  }

  @include mobile {
    column-gap: $mobile_spacing;
    grid-template-columns: 100%;
    grid-template-rows: auto;
  }
}

.veteran-card-type {
  margin-bottom: 1rem;
}

:deep(#spinner) {
  text-align: center;
}

:deep(.spinner) {
  div {
    &::after {
      top: 48px;
      left: 38px;
      width: 4px;
      height: 22px;
    }
  }
}

:deep(.modal-table) {
  width: 100%;
  margin-bottom: 0;
  text-align: right;

  tr {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  th,
  td {
    border-bottom: none;
    padding-left: 0;
  }

  .break-long-email {
    word-break: break-all;
  }

  a {
    margin-top: 10px;
  }
}
</style>
